import { Injectable } from '@angular/core';

export interface ExtensionMessageResponse {
    messageId: string;
    success: boolean;
    error?: string;
}

@Injectable({ providedIn: 'root' })
export class ExtensionMessageService {
    /**
     * Pinned extension origin for secure postMessage communication.
     * On Chrome, resolved immediately via ancestorOrigins — but only accepted if it is
     * actually an extension origin (chrome-extension://) to prevent postMessage data
     * leaks when the app is embedded in a non-extension iframe.
     * On Firefox, ancestorOrigins is unavailable; pinned on first trusted moz-extension://
     * message from the parent frame.
     */
    private pinnedExtensionOrigin: string | null = this.resolveInitialOrigin();

    private resolveInitialOrigin(): string | null {
        const origin = globalThis.location.ancestorOrigins?.[0];
        return origin?.startsWith('chrome-extension://') ? origin : null;
    }

    isExtensionContext(): boolean {
        return globalThis.parent !== globalThis.window;
    }

    getPinnedOrigin(): string | null {
        return this.pinnedExtensionOrigin;
    }

    /**
     * Validates and optionally pins the origin of an incoming message from the extension parent frame.
     * Only extension-scheme origins (chrome-extension://, moz-extension://) are trusted.
     * On Firefox, pins the origin on the first trusted moz-extension:// message.
     * Returns false if the origin is untrusted and the message should be ignored.
     */
    tryPinOrigin(event: MessageEvent): boolean {
        if (this.pinnedExtensionOrigin === null) {
            if (!event.origin.startsWith('moz-extension://') && !event.origin.startsWith('chrome-extension://')) {
                return false;
            }
            this.pinnedExtensionOrigin = event.origin;
        } else if (event.origin !== this.pinnedExtensionOrigin) {
            return false;
        }
        return true;
    }

    /**
     * Sends a message to the extension sidebar parent frame and resolves with the response.
     * Handles both Chrome (origin known immediately) and Firefox (two-step handshake).
     * Pass an AbortSignal to allow the caller to cancel the in-flight request immediately
     * (e.g. from ngOnDestroy) instead of waiting for the 5s timeout.
     */
    sendMessage(action: string, text: string, signal?: AbortSignal): Promise<ExtensionMessageResponse> {
        return new Promise((resolve) => {
            const messageId = crypto.randomUUID();
            let timeoutId: ReturnType<typeof setTimeout> | undefined;

            const cleanup = () => {
                clearTimeout(timeoutId);
                globalThis.removeEventListener('message', messageListener);
                signal?.removeEventListener('abort', onAbort);
            };

            const onAbort = () => {
                cleanup();
                resolve({ messageId, success: false, error: 'cancelled' });
            };

            const messageListener = (event: MessageEvent) => {
                if (event.source !== globalThis.parent) {
                    return;
                }

                if (!this.tryPinOrigin(event)) {
                    return;
                }

                if (event.data?.messageId !== messageId) {
                    return;
                }

                // Two-step handshake: sidebar signals it is ready to receive the text.
                // Origin is now pinned — resend with the actual text to the known origin.
                if (event.data?.readyForText === true) {
                    globalThis.parent.postMessage({ action, messageId, text }, this.pinnedExtensionOrigin);
                    return;
                }

                // When the sidebar needs a permission grant it replies immediately with
                // permission_required so it can show its own banner - but it will send a
                // second reply (success or error) with the same messageId once the user
                // has acted on that banner.  Keep the listener alive and extend the
                // timeout so that second reply can still be received and processed.
                if (event.data?.error === 'permission_required') {
                    clearTimeout(timeoutId);
                    timeoutId = setTimeout(() => {
                        cleanup();
                        resolve({ messageId, success: false, error: 'Timeout' });
                    }, 90000);
                    return;
                }

                cleanup();
                resolve(event.data as ExtensionMessageResponse);
            };

            if (signal?.aborted) {
                resolve({ messageId, success: false, error: 'cancelled' });
                return;
            }

            signal?.addEventListener('abort', onAbort, { once: true });
            globalThis.addEventListener('message', messageListener);

            if (this.pinnedExtensionOrigin === null) {
                // Firefox first call: send without text over '*' to avoid leaking content.
                // The sidebar replies with readyForText, pinning the origin before text is sent.
                globalThis.parent.postMessage({ action: 'handshake', messageId }, '*'); // NOSONAR
            } else {
                // Chrome or subsequent Firefox calls: origin known, send text directly.
                globalThis.parent.postMessage({ action, text, messageId }, this.pinnedExtensionOrigin);
            }

            timeoutId = setTimeout(() => {
                cleanup();
                resolve({ messageId, success: false, error: 'Timeout' });
            }, 5000);
        });
    }
}
